import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { MessageCircle, MapPin, Mail, Phone, Youtube, Facebook, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

// WhatsApp configuration
const WHATSAPP_NUMBER = "919433530797";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

// Asset paths
const ASSETS = {
  logo: "/assets/Lipika's creation logo.png",
  cover: "/assets/cover.jpg",
  silkMark: "/assets/silk-mark-logo.jpg",
  video: "/assets/brand-video.mp4",
  video2: "/assets/reel-video-2.mp4",
  video2Poster: "/assets/reel-video-2-poster.jpg",
  sareePhoto: "/assets/saree-photo.jpeg",
  collection1: "/assets/collection-1.webp",
  collection2: "/assets/collection-2.webp",
  collection3: "/assets/collection-3.webp",
  collection4: "/assets/collection-4.webp"
};

// Gallery images - four featured saree photos
const GALLERY_IMAGES = [
  {
    url: ASSETS.collection1,
    name: "Lavendar Purple Tussar with Gujrati stitch",
    alt: "Lipika Bhawal in a lavendar purple tussar silk saree with vibrant yellow Gujrati stitch embroidery — Lipika's Creation"
  },
  {
    url: ASSETS.collection2,
    name: "Dusty Rose Tussar with Gujrati stitch",
    alt: "Lipika Bhawal wearing a dusty rose tussar silk saree with traditional Gujrati stitch embroidery — Lipika's Creation"
  },
  {
    url: ASSETS.collection3,
    name: "Charcoal Silk with Hand-painted Florals",
    alt: "Lipika Bhawal in a charcoal grey silk saree with hand-painted yellow and red floral motifs — Lipika's Creation"
  },
  {
    url: ASSETS.collection4,
    name: "Pink Reshom Check with Gujrati stitch",
    alt: "Lipika Bhawal draped in a soft pink reshom check saree with intricate Gujrati stitch embroidery — Lipika's Creation"
  }
];

// Navigation links
const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Collection", href: "#collection" },
  { label: "Video", href: "#video" },
  { label: "Contact", href: "#contact" }
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header className={`header ${isScrolled ? "scrolled" : ""}`} data-testid="header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a href="#home" className="flex-shrink-0" data-testid="header-logo" aria-label="Lipika's Creation - Home">
              <img 
                src={ASSETS.logo} 
                alt="Lipika's Creation - Silk Mark Certified Saree Boutique in Kolkata" 
                className="h-14 md:h-16 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="nav-link"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Desktop WhatsApp CTA */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex header-whatsapp"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle size={16} />
              <span>Chat on WhatsApp</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              className={`hamburger lg:hidden ${isMobileMenuOpen ? "open" : ""}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`} data-testid="mobile-menu">
        {NAV_LINKS.map((link) => (
          <button
            key={link.href}
            onClick={() => handleNavClick(link.href)}
            className="mobile-nav-link"
            data-testid={`mobile-nav-${link.label.toLowerCase()}`}
          >
            {link.label}
          </button>
        ))}
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary flex items-center gap-2 mt-4"
          data-testid="mobile-whatsapp-btn"
        >
          <MessageCircle size={18} />
          Chat on WhatsApp
        </a>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const handleScrollToCollection = () => {
    document.querySelector("#collection")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative" data-testid="hero-section">
      {/* Cover Image Banner - Center-cropped to show branding */}
      <div className="w-full overflow-hidden bg-[#F8E8E0]" style={{ maxHeight: '220px' }}>
        <img
          src={ASSETS.cover}
          alt="Lipika's Creation boutique banner featuring Silk Mark certification and brand logo - Premium saree collection in Kolkata"
          className="w-full h-auto object-cover object-center"
          style={{ marginTop: '-20%', marginBottom: '-20%' }}
          data-testid="hero-image"
        />
      </div>
      
      {/* Content Below Image - Tight spacing */}
      <div className="py-6 md:py-10 bg-gradient-to-b from-[#F8E8E0] to-[#F4E3D7]">
        <div className="max-w-4xl mx-auto px-6 text-center animate-fade-in-up">
          <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#2D2424] font-light leading-tight mb-3">
            Timeless Sarees with<br />
            <span className="italic text-[#6A1E25]">Purity, Grace & Craftsmanship</span>
          </h1>
          <p className="text-[#5C4F4F] text-sm md:text-base mb-5 max-w-xl mx-auto font-light">
            Discover our curated collection of silk, tussar, and Bengal cotton sarees. 
            Each piece is woven with heritage and handcrafted with love.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2"
              data-testid="hero-whatsapp-btn"
              aria-label="Chat with Lipika's Creation on WhatsApp"
            >
              <MessageCircle size={18} aria-hidden="true" />
              Chat on WhatsApp
            </a>
            <button
              onClick={handleScrollToCollection}
              className="btn-outline"
              data-testid="hero-collection-btn"
              aria-label="View our saree collection"
            >
              View Collection
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Silk Mark Trust Section - Compact
const SilkMarkSection = () => {
  return (
    <section className="py-6 md:py-8 border-y border-[#E8E1D9] bg-[#FAF9F6]" data-testid="silkmark-section" aria-labelledby="silkmark-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
          <img
            src={ASSETS.silkMark}
            alt="Silk Mark India certification logo - Your assurance of pure silk authenticity"
            className="h-14 md:h-16 w-auto"
            data-testid="silkmark-logo"
          />
          <div className="text-center md:text-left">
            <h2 id="silkmark-heading" className="text-[#5C4F4F] text-xs md:text-sm tracking-[0.15em] uppercase font-medium">
              All sarees are Silk Mark certified
            </h2>
            <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mt-1">
              Your Assurance of Pure Silk
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section - Reduced spacing, better image display
const AboutSection = () => {
  return (
    <section id="about" className="about-section py-12 md:py-16" data-testid="about-section" aria-labelledby="about-heading">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image - Natural display, no aggressive cropping */}
          <div className="order-2 md:order-1 flex justify-center">
            <img
              src={ASSETS.sareePhoto}
              alt="Lipika Bhawal, founder of Lipika's Creation, wearing an elegant traditional silk saree with floral prints at her Kolkata boutique"
              className="w-full max-w-md h-auto object-contain rounded-sm shadow-lg"
              data-testid="about-image"
            />
          </div>

          {/* Content */}
          <div className="order-1 md:order-2">
            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-3">Our Story</p>
            <h2 id="about-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight mb-4">
              About Lipika's Creation
            </h2>
            <div className="decorative-line mb-6"></div>
            <div className="space-y-4 text-[#5C4F4F] text-sm md:text-base leading-relaxed font-light">
              <p>
                Lipika Bhawal established Lipika's Creation back in the year 2000. She was a College Lecturer who left her teaching profession as she was always in love with fashion and designing, especially when it comes to modern and traditional sarees.
              </p>
              <p>
                Now with a popular online and offline following, she is still striving to create the best collection of silk, tussar, cotton sarees. She is very particular about handstitched sarees and focusses on the purity of whatever she puts out there.
              </p>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 mt-6"
              data-testid="about-whatsapp-btn"
              aria-label="Connect with Lipika on WhatsApp"
            >
              <MessageCircle size={18} aria-hidden="true" />
              Connect with Lipika
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section - Autoplay enabled with two videos
const VideoSection = () => {
  const [isMuted1, setIsMuted1] = useState(true);
  const [isMuted2, setIsMuted2] = useState(true);
  const videoRef1 = useRef(null);
  const videoRef2 = useRef(null);

  const toggleMute1 = () => {
    if (videoRef1.current) {
      videoRef1.current.muted = !videoRef1.current.muted;
      setIsMuted1(videoRef1.current.muted);
    }
  };

  const toggleMute2 = () => {
    if (videoRef2.current) {
      videoRef2.current.muted = !videoRef2.current.muted;
      setIsMuted2(videoRef2.current.muted);
    }
  };

  return (
    <section id="video" className="py-10 md:py-16 bg-[#FAF9F6]" data-testid="video-section" aria-labelledby="video-heading">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-6 md:mb-8">
          <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-2">Experience</p>
          <h2 id="video-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight mb-2">
            Our Boutique in Motion
          </h2>
          <p className="text-[#5C4F4F] text-sm md:text-base max-w-xl mx-auto font-light">
            A closer look at the elegance, texture, and beauty of our saree collection.
          </p>
        </div>

        {/* First Video - Original */}
        <div className="relative w-full overflow-hidden shadow-xl bg-[#1a1a1a]" data-testid="video-container">
          <video
            ref={videoRef1}
            className="w-full h-auto block"
            style={{ maxHeight: '70vh' }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="brand-video"
            aria-label="Lipika's Creation boutique video showcasing silk, tussar, and Bengal cotton saree collection"
          >
            <source src={ASSETS.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <button
            onClick={toggleMute1}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-all duration-300"
            data-testid="video-mute-btn"
            aria-label={isMuted1 ? "Unmute video" : "Mute video"}
          >
            {isMuted1 ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <line x1="23" y1="9" x2="17" y2="15"></line>
                <line x1="17" y1="9" x2="23" y2="15"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
            )}
          </button>
        </div>

        {/* Second Video - Below the first */}
        <div className="relative w-full max-w-md mx-auto overflow-hidden shadow-xl bg-[#1a1a1a] mt-6 md:mt-8 aspect-[9/16]" data-testid="video-container-2">
          <video
            ref={videoRef2}
            className="absolute inset-0 w-full h-full object-cover block"
            poster={ASSETS.video2Poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="brand-video-2"
            aria-label="Lipika's Creation saree showcase"
          >
            <source src={ASSETS.video2} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <button
            onClick={toggleMute2}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-all duration-300"
            data-testid="video-mute-btn-2"
            aria-label={isMuted2 ? "Unmute video" : "Mute video"}
          >
            {isMuted2 ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <line x1="23" y1="9" x2="17" y2="15"></line>
                <line x1="17" y1="9" x2="23" y2="15"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

// Collection Section — 4 sarees with lightbox + per-saree WhatsApp enquiry
const CollectionSection = () => {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const showPrev = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };
  const showNext = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) => (i + 1) % GALLERY_IMAGES.length);
  };

  // Keyboard controls + body scroll lock while open
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx]);

  const buildEnquiryUrl = (sareeName) => {
    const msg = `Hi Lipika, I'm interested in the "${sareeName}" saree from your collection. Could you share more details please?`;
    return `${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`;
  };

  const active = lightboxIdx !== null ? GALLERY_IMAGES[lightboxIdx] : null;

  return (
    <section id="collection" className="py-12 md:py-16 bg-[#FAF9F6]" data-testid="collection-section" aria-labelledby="collection-heading">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8 md:mb-10">
          <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-2">Curated Selection</p>
          <h2 id="collection-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight mb-2">
            Our Collection
          </h2>
          <p className="text-[#5C4F4F] text-sm md:text-base max-w-xl mx-auto font-light">
            Each saree tells a story of heritage, woven with tradition and adorned with elegance.
          </p>
        </div>

        {/* Four Featured Sarees - Responsive Grid */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
          data-testid="collection-grid"
        >
          {GALLERY_IMAGES.map((img, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden bg-[#F3EDE4] shadow-md hover:shadow-xl transition-shadow duration-500"
              data-testid={`collection-item-${idx}`}
            >
              {/* Image (click to open lightbox) */}
              <button
                type="button"
                onClick={() => openLightbox(idx)}
                className="block w-full aspect-[2/3] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7A1F1F]"
                aria-label={`View larger image of ${img.name}`}
                data-testid={`collection-zoom-${idx}`}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                {/* Zoom hint on hover (desktop) */}
                <span
                  className="hidden md:flex absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  aria-hidden="true"
                >
                  <ZoomIn size={16} color="white" />
                </span>
              </button>

              {/* Saree name + per-item WhatsApp button */}
              <div className="p-2.5 sm:p-3 bg-white flex flex-col gap-2">
                <p
                  className="font-['Cormorant_Garamond'] text-[#2D2424] text-sm sm:text-base leading-snug line-clamp-2"
                  data-testid={`collection-name-${idx}`}
                >
                  {img.name}
                </p>
                <a
                  href={buildEnquiryUrl(img.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 w-full text-[11px] sm:text-xs tracking-wider uppercase font-medium py-2 px-2 rounded-sm border border-[#7A1F1F] text-[#7A1F1F] hover:bg-[#7A1F1F] hover:text-white transition-colors duration-300"
                  data-testid={`collection-whatsapp-${idx}`}
                  aria-label={`Enquire about ${img.name} on WhatsApp`}
                >
                  <MessageCircle size={13} aria-hidden="true" />
                  Enquire
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="collection-whatsapp-btn"
            aria-label="Enquire about our saree collection on WhatsApp"
          >
            <MessageCircle size={18} aria-hidden="true" />
            Enquire About Collection
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center px-4 py-6 animate-[fadeIn_200ms_ease-out]"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${active.name} — expanded view`}
          data-testid="collection-lightbox"
        >
          {/* Close */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
            aria-label="Close image viewer"
            data-testid="lightbox-close"
          >
            <X size={22} />
          </button>

          {/* Prev */}
          <button
            type="button"
            onClick={showPrev}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
            aria-label="Previous saree"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Image + caption */}
          <div
            className="relative max-w-[92vw] md:max-w-3xl max-h-[85vh] flex flex-col items-center gap-3 md:gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.url}
              alt={active.alt}
              className="max-w-full max-h-[70vh] md:max-h-[75vh] w-auto h-auto object-contain shadow-2xl"
              data-testid="lightbox-image"
            />
            <div className="text-center px-2">
              <p className="font-['Cormorant_Garamond'] text-white text-lg md:text-xl mb-2">
                {active.name}
              </p>
              <a
                href={buildEnquiryUrl(active.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5C] text-white text-xs md:text-sm tracking-wider uppercase font-medium py-2.5 px-5 rounded-full transition-colors duration-300"
                data-testid="lightbox-whatsapp"
              >
                <MessageCircle size={16} aria-hidden="true" />
                Enquire on WhatsApp
              </a>
            </div>
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={showNext}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
            aria-label="Next saree"
            data-testid="lightbox-next"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </section>
  );
};
const WhyChooseUsSection = () => {
  const features = [
    {
      title: "Silk Mark Certified",
      description: "Every saree comes with Silk Mark certification, guaranteeing pure silk authenticity."
    },
    {
      title: "Curated Collection",
      description: "Hand-picked silk, tussar, and Bengal cotton sarees from master weavers."
    },
    {
      title: "Purity & Craftsmanship",
      description: "Focus on handstitched sarees with uncompromising quality and attention to detail."
    },
    {
      title: "Personal Attention",
      description: "Experience boutique-style personal service and styling guidance."
    },
    {
      title: "Heritage Meets Modern",
      description: "Traditional elegance combined with contemporary taste for the modern woman."
    }
  ];

  return (
    <section className="py-10 md:py-14 bg-[#F4E3D7]" data-testid="why-choose-section" aria-labelledby="why-choose-heading">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-2">Our Promise</p>
          <h2 id="why-choose-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight">
            Why Choose Lipika's Creation
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <article
              key={index}
              className="text-center p-4"
              data-testid={`feature-${index}`}
            >
              <div className="w-10 h-[2px] bg-gradient-to-r from-[#C5A059] to-[#6A1E25] mx-auto mb-4" aria-hidden="true"></div>
              <h3 className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#2D2424] mb-2">
                {feature.title}
              </h3>
              <p className="text-[#5C4F4F] text-sm font-light">
                {feature.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Social Section - Compact
const SocialSection = () => {
  return (
    <section className="py-10 md:py-14 bg-[#FAF9F6]" data-testid="social-section" aria-labelledby="social-heading">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-2">Stay Connected</p>
          <h2 id="social-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight mb-2">
            Join Our Community
          </h2>
          <p className="text-[#5C4F4F] text-sm md:text-base max-w-xl mx-auto font-light">
            Follow our latest live saree showcases and new arrivals
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* YouTube */}
          <a
            href="https://www.youtube.com/@lipikascreation7207"
            target="_blank"
            rel="noopener noreferrer"
            className="social-card group"
            data-testid="youtube-card"
            aria-label="Watch Lipika's Creation on YouTube - Live showcases and saree styling tips"
          >
            <div className="w-12 h-12 rounded-full bg-[#FF0000]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#FF0000]/20 transition-colors">
              <Youtube size={22} className="text-[#FF0000]" aria-hidden="true" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2D2424] mb-1">
              Watch us on YouTube
            </h3>
            <p className="text-[#5C4F4F] text-xs font-light">
              Live showcases & saree styling tips
            </p>
          </a>

          {/* Facebook */}
          <a
            href="https://www.facebook.com/lipikascreation/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-card group"
            data-testid="facebook-card"
            aria-label="Follow Lipika's Creation on Facebook - Latest collections and updates"
          >
            <div className="w-12 h-12 rounded-full bg-[#1877F2]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1877F2]/20 transition-colors">
              <Facebook size={22} className="text-[#1877F2]" aria-hidden="true" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2D2424] mb-1">
              Follow us on Facebook
            </h3>
            <p className="text-[#5C4F4F] text-xs font-light">
              Latest collections & updates
            </p>
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section - Compact
const ContactSection = () => {
  return (
    <section id="contact" className="contact-section py-12 md:py-16" data-testid="contact-section" aria-labelledby="contact-heading">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-2">Get in Touch</p>
          <h2 id="contact-heading" className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D2424] font-light leading-tight mb-2">
            Let's Connect
          </h2>
          <p className="text-[#5C4F4F] text-sm md:text-base max-w-xl mx-auto font-light">
            Ready to find your perfect saree? Reach out to us for personalized assistance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* WhatsApp CTA */}
          <div className="bg-[#FAF9F6] p-6 md:p-8 text-center border border-[#E8E1D9]" data-testid="whatsapp-contact-card">
            <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={26} className="text-white" aria-hidden="true" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#2D2424] mb-2">
              Chat with us on WhatsApp
            </h3>
            <p className="text-[#5C4F4F] text-sm mb-4 font-light">
              Quick responses for orders, enquiries, and bookings
            </p>
            <p className="text-[#C5A059] text-base font-medium mb-4">
              +91-9433530797
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E]"
              data-testid="contact-whatsapp-btn"
              aria-label="Book your saree via WhatsApp"
            >
              <MessageCircle size={18} aria-hidden="true" />
              Book via WhatsApp
            </a>
          </div>

          {/* Contact Details */}
          <address className="space-y-5 not-italic" data-testid="contact-details">
            {/* Address */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6A1E25]/10 flex items-center justify-center">
                <MapPin size={18} className="text-[#6A1E25]" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-lg text-[#2D2424] mb-1">Visit Our Boutique</h3>
                <p className="text-[#5C4F4F] text-sm font-light">
                  P-166A, Scheme 6M, Kankurgachi,<br />
                  Kolkata 700054, West Bengal, India
                </p>
                <p className="text-[#C5A059] text-xs mt-1 italic font-medium">
                  Visit only with appointment
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6A1E25]/10 flex items-center justify-center">
                <Mail size={18} className="text-[#6A1E25]" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-lg text-[#2D2424] mb-1">Email Us</h3>
                <a 
                  href="mailto:lipikascreation@gmail.com" 
                  className="text-[#5C4F4F] text-sm font-light hover:text-[#C5A059] transition-colors"
                >
                  lipikascreation@gmail.com
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6A1E25]/10 flex items-center justify-center">
                <Phone size={18} className="text-[#6A1E25]" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-lg text-[#2D2424] mb-1">Call / WhatsApp</h3>
                <a 
                  href="tel:+919433530797" 
                  className="text-[#5C4F4F] text-sm font-light hover:text-[#C5A059] transition-colors"
                >
                  +91-9433530797
                </a>
              </div>
            </div>
          </address>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer py-12 md:py-16" data-testid="footer" role="contentinfo">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo */}
          <div className="text-center md:text-left">
            <img
              src={ASSETS.logo}
              alt="Lipika's Creation - Silk Mark Certified Saree Boutique"
              className="h-16 w-auto mx-auto md:mx-0 brightness-0 invert opacity-90"
              data-testid="footer-logo"
            />
          </div>

          {/* Contact */}
          <div className="text-center">
            <p className="text-[#C5A059] text-sm tracking-[0.15em] uppercase mb-2">
              Silk Mark Certified Boutique
            </p>
            <p className="text-[#FAF9F6]/70 text-sm font-light">
              Kankurgachi, Kolkata, India
            </p>
          </div>

          {/* Social Links */}
          <nav className="flex justify-center md:justify-end gap-4" aria-label="Social media links">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF9F6]/10 flex items-center justify-center hover:bg-[#25D366] transition-colors"
              data-testid="footer-whatsapp"
              aria-label="Chat with Lipika's Creation on WhatsApp"
            >
              <MessageCircle size={18} className="text-[#FAF9F6]" aria-hidden="true" />
            </a>
            <a
              href="https://www.youtube.com/@lipikascreation7207"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF9F6]/10 flex items-center justify-center hover:bg-[#FF0000] transition-colors"
              data-testid="footer-youtube"
              aria-label="Watch Lipika's Creation on YouTube"
            >
              <Youtube size={18} className="text-[#FAF9F6]" aria-hidden="true" />
            </a>
            <a
              href="https://www.facebook.com/lipikascreation/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF9F6]/10 flex items-center justify-center hover:bg-[#1877F2] transition-colors"
              data-testid="footer-facebook"
              aria-label="Follow Lipika's Creation on Facebook"
            >
              <Facebook size={18} className="text-[#FAF9F6]" aria-hidden="true" />
            </a>
          </nav>
        </div>

        {/* Divider */}
        <div className="h-[1px] bg-[#FAF9F6]/10 my-8" aria-hidden="true"></div>

        {/* Copyright */}
        <div className="text-center">
          <p className="text-[#FAF9F6]/50 text-sm font-light">
            &copy; {currentYear} Lipika's Creation. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppFloat = () => {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-float-btn"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle size={28} />
    </a>
  );
};

// Main App Component - Video moved higher in the page
function App() {
  return (
    <div className="App min-h-screen bg-[#FAF9F6]">
      <Header />
      <main>
        <HeroSection />
        <SilkMarkSection />
        <VideoSection />
        <AboutSection />
        <CollectionSection />
        <WhyChooseUsSection />
        <SocialSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
